/**
 * tdesign v1.0.3
 * (c) 2023 tdesign
 * @license MIT
 */

import { _ as _defineProperty } from '../_chunks/dep-46de7b2e.js';
import { _ as _slicedToArray } from '../_chunks/dep-e43cf21c.js';
import React, { forwardRef, useState, useRef, useMemo, useEffect, useImperativeHandle } from 'react';
import { CSSTransition } from 'react-transition-group';
import { i as isFunction_1 } from '../_chunks/dep-f10515e8.js';
import classNames from 'classnames';
import { usePopper } from 'react-popper';
import useControlled from '../hooks/useControlled.js';
import useAnimation from '../_util/useAnimation.js';
import useConfig from '../hooks/useConfig.js';
import usePrevious from '../hooks/usePrevious.js';
import Portal from '../common/Portal.js';
import useTrigger from './hooks/useTrigger.js';
import { getRefDom } from './utils/ref.js';
import { getTransitionParams } from './utils/transition.js';
import useMutationObservable from '../_util/useMutationObserver.js';
import useWindowSize from '../_util/useWindowSize.js';
import { popupDefaultProps } from './defaultProps.js';
import '../_chunks/dep-2acfafca.js';
import '../_chunks/dep-c3f1141e.js';
import '../_chunks/dep-4941ebe7.js';
import '../_chunks/dep-e9b62d1f.js';
import '../_chunks/dep-88cd50bf.js';
import '../_chunks/dep-f721d103.js';
import '../_chunks/dep-4559db64.js';
import '../_chunks/dep-c00532d4.js';
import '../_chunks/dep-33ffecad.js';
import '../_chunks/dep-77e34309.js';
import '../_chunks/dep-c1f93350.js';
import '../_chunks/dep-7dce2c26.js';
import '../_util/noop.js';
import '../config-provider/ConfigContext.js';
import '../_chunks/dep-d71ee5f3.js';
import '../_chunks/dep-4b8f240e.js';
import '../_chunks/dep-9acbc163.js';
import '../_chunks/dep-7b52f080.js';
import '../_chunks/dep-dcb5aa2a.js';
import '../_chunks/dep-ee4bf8c2.js';
import '../_chunks/dep-934eda0c.js';
import '../_chunks/dep-12510d64.js';
import '../_chunks/dep-6b568d83.js';
import '../_chunks/dep-304e657b.js';
import '../_chunks/dep-6506fbc2.js';
import '../_chunks/dep-bb5d7b1e.js';
import '../_chunks/dep-e285f2f8.js';
import '../_chunks/dep-f4a320cd.js';
import '../_chunks/dep-ec70b95c.js';
import '../locale/zh_CN.js';
import '../_common/js/global-config/locale/zh_CN.js';
import 'dayjs';
import '../_chunks/dep-cbcc9f89.js';
import '../_common/js/global-config/default-config.js';
import 'react-dom';
import '../_util/dom.js';
import 'raf';
import '../_chunks/dep-6c5254c6.js';
import '../_util/easing.js';
import 'react-is';
import '../_util/composeRefs.js';
import '../_chunks/dep-01ce608f.js';
import '../_chunks/dep-77a54538.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var Popup = /*#__PURE__*/forwardRef(function (props, ref) {
  var trigger = props.trigger,
    content = props.content,
    placement = props.placement,
    attach = props.attach,
    showArrow = props.showArrow,
    destroyOnClose = props.destroyOnClose,
    overlayClassName = props.overlayClassName,
    overlayInnerClassName = props.overlayInnerClassName,
    overlayStyle = props.overlayStyle,
    overlayInnerStyle = props.overlayInnerStyle,
    triggerElement = props.triggerElement,
    _props$children = props.children,
    children = _props$children === void 0 ? triggerElement : _props$children,
    disabled = props.disabled,
    zIndex = props.zIndex,
    onScroll = props.onScroll,
    onScrollToBottom = props.onScrollToBottom,
    expandAnimation = props.expandAnimation,
    delay = props.delay,
    hideEmptyPopup = props.hideEmptyPopup,
    popperOptions = props.popperOptions,
    updateScrollTop = props.updateScrollTop;
  var _useConfig = useConfig(),
    classPrefix = _useConfig.classPrefix;
  var _useAnimation = useAnimation(),
    keepExpand = _useAnimation.keepExpand,
    keepFade = _useAnimation.keepFade;
  var _useWindowSize = useWindowSize(),
    windowHeight = _useWindowSize.height,
    windowWidth = _useWindowSize.width;
  var _useControlled = useControlled(props, "visible", props.onVisibleChange),
    _useControlled2 = _slicedToArray(_useControlled, 2),
    visible = _useControlled2[0],
    onVisibleChange = _useControlled2[1];
  var prevVisible = usePrevious(visible);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    popupElement = _useState2[0],
    setPopupElement = _useState2[1];
  var triggerRef = useRef(null);
  var popupRef = useRef(null);
  var portalRef = useRef(null);
  var contentRef = useRef(null);
  var popperRef = useRef(null);
  var DEFAULT_TRANSITION_TIMEOUT = 180;
  var showOverlay = useMemo(function () {
    if (hideEmptyPopup && !content) return false;
    return visible || popupElement;
  }, [hideEmptyPopup, content, visible, popupElement]);
  var popperPlacement = useMemo(function () {
    return placement.replace(/-(left|top)$/, "-start").replace(/-(right|bottom)$/, "-end");
  }, [placement]);
  var _useTrigger = useTrigger({
      triggerRef: triggerRef,
      content: content,
      disabled: disabled,
      trigger: trigger,
      visible: visible,
      delay: delay,
      onVisibleChange: onVisibleChange
    }),
    getTriggerNode = _useTrigger.getTriggerNode,
    getPopupProps = _useTrigger.getPopupProps,
    getTriggerDom = _useTrigger.getTriggerDom;
  var triggerNode = isFunction_1(children) ? getTriggerNode(children({
    visible: visible
  })) : getTriggerNode(children);
  var updateTimeRef = useRef(null);
  useMutationObservable(getRefDom(triggerRef), function () {
    clearTimeout(updateTimeRef.current);
    updateTimeRef.current = setTimeout(function () {
      var _popperRef$current, _popperRef$current$up;
      return (_popperRef$current = popperRef.current) === null || _popperRef$current === void 0 ? void 0 : (_popperRef$current$up = _popperRef$current.update) === null || _popperRef$current$up === void 0 ? void 0 : _popperRef$current$up.call(_popperRef$current);
    }, 0);
  });
  useEffect(function () {
    return function () {
      return clearTimeout(updateTimeRef.current);
    };
  }, []);
  useEffect(function () {
    var _popperRef$current2, _popperRef$current2$u;
    (_popperRef$current2 = popperRef.current) === null || _popperRef$current2 === void 0 ? void 0 : (_popperRef$current2$u = _popperRef$current2.update) === null || _popperRef$current2$u === void 0 ? void 0 : _popperRef$current2$u.call(_popperRef$current2);
  }, [visible, content, windowHeight, windowWidth]);
  useEffect(function () {
    if (!triggerRef.current) triggerRef.current = getTriggerDom();
    if (prevVisible !== visible && visible) {
      updateScrollTop === null || updateScrollTop === void 0 ? void 0 : updateScrollTop(contentRef.current);
    }
  }, [visible, prevVisible, updateScrollTop, getTriggerDom]);
  function handleExited() {
    !destroyOnClose && popupElement && (popupElement.style.display = "none");
  }
  function handleEnter() {
    !destroyOnClose && popupElement && (popupElement.style.display = "block");
  }
  function handleScroll(e) {
    var _e$target = e.target,
      scrollTop = _e$target.scrollTop,
      clientHeight = _e$target.clientHeight,
      scrollHeight = _e$target.scrollHeight;
    if (scrollHeight - scrollTop === clientHeight) {
      onScrollToBottom === null || onScrollToBottom === void 0 ? void 0 : onScrollToBottom({
        e: e
      });
    }
    onScroll === null || onScroll === void 0 ? void 0 : onScroll({
      e: e
    });
  }
  popperRef.current = usePopper(getRefDom(triggerRef), popupElement, _objectSpread({
    placement: popperPlacement
  }, popperOptions));
  var _popperRef$current3 = popperRef.current,
    styles = _popperRef$current3.styles,
    attributes = _popperRef$current3.attributes;
  function getOverlayStyle(overlayStyle2) {
    if (getRefDom(triggerRef) && popupRef.current && typeof overlayStyle2 === "function") {
      return _objectSpread({}, overlayStyle2(getRefDom(triggerRef), popupRef.current));
    }
    return _objectSpread({}, overlayStyle2);
  }
  var overlay = showOverlay && /* @__PURE__ */React.createElement(CSSTransition, {
    appear: true,
    "in": visible,
    timeout: DEFAULT_TRANSITION_TIMEOUT,
    nodeRef: portalRef,
    unmountOnExit: destroyOnClose,
    onEnter: handleEnter,
    onExited: handleExited
  }, /* @__PURE__ */React.createElement(Portal, {
    triggerNode: getRefDom(triggerRef),
    attach: attach,
    ref: portalRef
  }, /* @__PURE__ */React.createElement(CSSTransition, _objectSpread({
    appear: true,
    timeout: 0,
    "in": visible,
    nodeRef: popupRef
  }, getTransitionParams({
    classPrefix: classPrefix,
    fadeAnimation: keepFade,
    expandAnimation: expandAnimation && keepExpand
  })), /* @__PURE__ */React.createElement("div", _objectSpread(_objectSpread({
    ref: function ref(node) {
      if (node) {
        popupRef.current = node;
        setPopupElement(node);
      }
    },
    style: _objectSpread(_objectSpread({}, styles.popper), {}, {
      zIndex: zIndex
    }, getOverlayStyle(overlayStyle)),
    className: classNames("".concat(classPrefix, "-popup"), overlayClassName)
  }, attributes.popper), getPopupProps()), /* @__PURE__ */React.createElement("div", {
    ref: contentRef,
    className: classNames("".concat(classPrefix, "-popup__content"), _defineProperty({}, "".concat(classPrefix, "-popup__content--arrow"), showArrow), overlayInnerClassName),
    style: getOverlayStyle(overlayInnerStyle),
    onScroll: handleScroll
  }, showArrow ? /* @__PURE__ */React.createElement("div", {
    style: styles.arrow,
    className: "".concat(classPrefix, "-popup__arrow")
  }) : null, content)))));
  useImperativeHandle(ref, function () {
    return {
      getPopper: function getPopper() {
        return popperRef.current;
      },
      getPopupElement: function getPopupElement() {
        return popupRef.current;
      },
      getPortalElement: function getPortalElement() {
        return portalRef.current;
      },
      getPopupContentElement: function getPopupContentElement() {
        return contentRef.current;
      },
      setVisible: function setVisible(visible2) {
        return onVisibleChange(visible2, {
          trigger: "document"
        });
      }
    };
  });
  return /* @__PURE__ */React.createElement(React.Fragment, null, triggerNode, overlay);
});
Popup.displayName = "Popup";
Popup.defaultProps = popupDefaultProps;

export { Popup as default };
//# sourceMappingURL=Popup.js.map
